import { type PayloadAction, createReducer } from '@reduxjs/toolkit';
import { exclusivePublicSizeCalcEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicSizes';
import { removeFileSuccess } from 'reactApp/modules/modifying/modifying.actions';
import type { RemoveFileSuccessAction } from 'reactApp/modules/modifying/modifying.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { Reducer } from 'redux';

import type {
    IRange,
    IRemove,
    ISelectOne,
    ISelectSeveral,
    ISelectSome,
    ISelectTo,
    IToggle,
    IToggleAll,
    ScrollToItemState,
    State,
} from './selection.types';
import {
    remove,
    resetSelect,
    selectOne,
    selectSeveral,
    selectSome,
    selectTo,
    setDragging,
    setScrollToItemState,
    startSelecting,
    toggle,
    toggleAll,
} from './selections.actions';

const initialState: State = {
    selected: {},
    isDragging: false,
    lastSelectedIdx: '',
    isSelecting: false,
    scrollToItemState: { itemId: '', needScroll: false },
    isTotalAllSelected: false,
    selectionRange: {
        offset: 0,
        rangeStartId: '',
    },
};

export const selectionsReducer: Reducer = createReducer(initialState, {
    [selectOne.type]: (state, action: PayloadAction<ISelectOne>) => {
        const { selectedIdx, storage } = action.payload;

        return {
            ...state,
            storage,
            selected: {
                [selectedIdx]: true,
            },
            lastSelectedIdx: selectedIdx,
            isTotalAllSelected: false,
            selectionRange: {
                rangeStartId: selectedIdx,
                offset: 0,
            },
        };
    },
    [selectSeveral.type]: (state, action: PayloadAction<ISelectSeveral>) => {
        const { selectedIdxs, storage } = action.payload;

        return {
            ...state,
            storage,
            selected: selectedIdxs.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
            isTotalAllSelected: false,
            lastSelectedIdx: selectedIdxs[selectedIdxs.length - 1],
        };
    },
    [resetSelect.type]: () => {
        return initialState;
    },
    [removeFileSuccess.type]: (state, action: PayloadAction<RemoveFileSuccessAction>) => {
        const selected = { ...state.selected };
        action.payload.ids.forEach((id) => {
            delete selected[id];
        });

        return {
            ...state,
            selected,
            selectionRange: {
                rangeStartId: '',
                offset: 0,
            },
        };
    },
    [selectSome.type]: (state, action: PayloadAction<ISelectSome>) => {
        const { rangeStartId = state.selectionRange?.rangeStartId || '', offset = 0 } = action.payload.selectionRange || {};

        return {
            ...state,
            lastSelectedIdx: '',
            storage: action.payload.storage,
            selected: action.payload.selectedIdxs.reduce(
                (result, item) => ({
                    ...result,
                    [item]: true,
                }),
                {}
            ),
            isTotalAllSelected: false,
            selectionRange: {
                rangeStartId,
                offset,
            },
        };
    },
    [toggle.type]: (state, action: PayloadAction<IToggle>) => {
        const selected = { ...state.selected };
        let lastSelectedIdx = state.lastSelectedIdx;
        let selectionRange: IRange | null = null;
        const { selectedIdx, storage } = action.payload;

        if (selected[selectedIdx]) {
            delete selected[selectedIdx];
        } else {
            selected[selectedIdx] = true;
            lastSelectedIdx = selectedIdx;
            selectionRange = {
                rangeStartId: selectedIdx,
                offset: 0,
            };
        }

        return {
            ...state,
            selected,
            isTotalAllSelected: false,
            lastSelectedIdx,
            storage,
            selectionRange,
        };
    },
    [remove.type]: (state, action: PayloadAction<IRemove>) => {
        const { selectedIdx } = action.payload;
        const selected = { ...state.selected };

        if (selected[selectedIdx]) {
            delete selected[selectedIdx];
        }

        return {
            ...state,
            selected,
        };
    },
    [toggleAll.type]: (state, action: PayloadAction<IToggleAll>) => {
        const { allIdxs, storage } = action.payload;

        const selectAll = Object.keys(state.selected).length === allIdxs.length;
        const lastSelectedIdx = selectAll ? allIdxs[allIdxs.length - 1] : '';

        return {
            ...state,
            lastSelectedIdx,
            storage,
            selectionRange: {
                rangeStartId: lastSelectedIdx,
                offset: 0,
            },
            isTotalAllSelected:
                exclusivePublicSizeCalcEnabled && (storage === EStorageType.home || storage === EStorageType.public) && !selectAll,
            selected: selectAll
                ? {}
                : allIdxs.reduce(
                      (result, item) => ({
                          ...result,
                          [item]: true,
                      }),
                      {}
                  ),
        };
    },
    [selectTo.type]: (state, action: PayloadAction<ISelectTo>) => {
        const lastSelectedIndex = action.payload.allIdxs.indexOf(state.lastSelectedIdx);
        const currentIndex = action.payload.allIdxs.indexOf(action.payload.currentId);
        const alreadySelected = state.selected[action.payload.currentId];
        let ids: string[] = [];

        if (alreadySelected) {
            return state;
        }

        if (lastSelectedIndex !== -1 && !alreadySelected) {
            ids = action.payload.allIdxs.slice(
                lastSelectedIndex < currentIndex ? lastSelectedIndex : currentIndex,
                (lastSelectedIndex > currentIndex ? lastSelectedIndex : currentIndex) + 1
            );
        } else {
            ids = action.payload.allIdxs.slice(0, currentIndex + 1);
        }

        return {
            ...state,
            storage: action.payload.storage,
            lastSelectedIdx: action.payload.currentId,
            selectionRange: {
                rangeStartId: action.payload.currentId,
                offset: 0,
            },
            isTotalAllSelected: false,
            selected: {
                ...state.selected,
                ...ids.reduce(
                    (result, item) => ({
                        ...result,
                        [item]: true,
                    }),
                    {}
                ),
            },
        };
    },
    [setDragging.type]: (state, action: PayloadAction<boolean>) => {
        state.isDragging = action.payload;
    },
    [startSelecting.type]: (state) => {
        state.isSelecting = true;
    },
    [setScrollToItemState.type]: (state, action: PayloadAction<ScrollToItemState>) => {
        const { payload } = action;
        state.scrollToItemState = payload;
    },
});
